// The agent chat's transcript, built from a turn's events (pure, unit-tested without a browser). The
// same reduction as the host's chat panel, over the events an agent turn produces.
import type { AgentTurnEvent } from './host.tsx';

export interface ToolChip {
  id: string;
  agent: string;
  name: string;
  status: 'running' | 'done' | 'failed';
  summary?: string;
  ms?: number;
}

export type Item =
  | { kind: 'user'; id: string; text: string }
  | {
      kind: 'assistant';
      id: string;
      text: string;
      /** Streaming until the turn's `done`. */
      complete: boolean;
      tools: ToolChip[];
      /** The persona working right now (a delegate's progress). */
      working: string | null;
      status?: 'ok' | 'refused' | 'error' | 'stopped';
      usage?: { inputTokens: number; outputTokens: number; costUsd: number | null };
      /** How the run ended (ADR 0039 §4), what stopped it, and a call that waits for approval. */
      outcome?: string;
      stop?: { reason: string; budget?: string; check?: string };
      approval?: { tool: string; input: Record<string, unknown>; key: string };
      /** The message this answers (an approval sends it again). */
      prompt?: string;
    }
  | { kind: 'error'; id: string; text: string };

let n = 0;
const uid = (p: string) => `${p}${Date.now().toString(36)}${(n++).toString(36)}`;

/** A new turn: the user's message and an empty, streaming answer. */
export function startTurn(items: Item[], text: string): Item[] {
  return [
    ...items,
    { kind: 'user', id: uid('u'), text },
    { kind: 'assistant', id: uid('a'), text: '', complete: false, tools: [], working: null, prompt: text },
  ];
}

/** Applies one event to the turn in progress (the last assistant item). */
export function applyEvent(items: Item[], e: AgentTurnEvent): Item[] {
  if (e.type === 'error') {
    const out = items.map((it, i) =>
      i === items.length - 1 && it.kind === 'assistant' && !it.complete
        ? { ...it, complete: true, working: null, status: 'error' as const }
        : it,
    );
    return [...out, { kind: 'error', id: uid('e'), text: e.message }];
  }
  const last = items.at(-1);
  if (!last || last.kind !== 'assistant' || last.complete) return items;
  const next = { ...last };
  switch (e.type) {
    case 'run_start':
      next.working = e.parent ? e.label : next.working;
      break;
    case 'run_end':
      if (e.agent !== 'orchestrator') next.working = null;
      break;
    case 'tool_call':
      next.tools = [...next.tools, { id: e.id, agent: e.agent, name: e.name, status: 'running' }];
      break;
    case 'tool_result':
      next.tools = next.tools.map((t) =>
        t.id === e.id ? { ...t, status: e.status, summary: e.summary, ms: e.ms } : t,
      );
      break;
    case 'text':
      next.text += e.delta;
      break;
    case 'done':
      next.text = e.text;
      next.complete = true;
      next.working = null;
      next.status = e.status;
      if (e.usage) next.usage = e.usage;
      if (e.outcome) next.outcome = e.outcome;
      if (e.stop) next.stop = e.stop;
      if (e.approval) next.approval = e.approval;
      break;
    default:
      return items;
  }
  return [...items.slice(0, -1), next];
}

/** Ends a turn the person stopped (the stream was cut). */
export function stopTurn(items: Item[]): Item[] {
  const last = items.at(-1);
  if (!last || last.kind !== 'assistant' || last.complete) return items;
  return [
    ...items.slice(0, -1),
    {
      ...last,
      complete: true,
      working: null,
      status: 'stopped',
      tools: last.tools.map((t) => (t.status === 'running' ? { ...t, status: 'failed' as const } : t)),
    },
  ];
}

/** An answer's tokens as people read them: `14k tokens` (in and out together). */
export const formatUsage = (u: { inputTokens: number; outputTokens: number; costUsd: number | null }) => {
  const n = u.inputTokens + u.outputTokens;
  return `${n >= 1000 ? `${Math.round(n / 1000)}k` : n} tokens`;
};
